import { useState, useCallback } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CapabilityTab({ projectId, token, datasets, selectedDataset, setSelectedDataset, getNumericColumns }) {
  const [capColumn, setCapColumn] = useState("");
  const [lsl, setLsl] = useState("");
  const [usl, setUsl] = useState("");
  const [capResult, setCapResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const runCapabilityAnalysis = useCallback(async () => {
    if (!selectedDataset || !capColumn || !lsl || !usl) {
      toast.error("Please fill all fields");
      return;
    }
    setAnalyzing(true);
    try {
      const response = await fetch(
        `${API}/projects/${projectId}/datasets/${selectedDataset.dataset_id}/capability?column=${capColumn}&lsl=${lsl}&usl=${usl}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Analysis failed");
      }
      const data = await response.json();
      setCapResult(data);
      toast.success("Capability analysis complete");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAnalyzing(false);
    }
  }, [projectId, token, selectedDataset, capColumn, lsl, usl]);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h3 className="font-heading font-semibold mb-4">Process Capability Analysis</h3>

      <div className="grid md:grid-cols-5 gap-4 mb-6">
        <div className="space-y-2">
          <Label>Dataset</Label>
          <select
            value={selectedDataset?.dataset_id || ""}
            onChange={(e) => {
              const ds = datasets.find((d) => d.dataset_id === e.target.value);
              setSelectedDataset(ds);
              setCapColumn("");
              setCapResult(null);
            }}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            data-testid="cap-dataset-select"
          >
            <option value="">Select dataset</option>
            {datasets.map((ds) => (
              <option key={ds.dataset_id} value={ds.dataset_id}>{ds.filename}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label>Measure Column</Label>
          <select
            value={capColumn}
            onChange={(e) => setCapColumn(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            disabled={!selectedDataset}
            data-testid="cap-column-select"
          >
            <option value="">Select column</option>
            {getNumericColumns().map((col) => (
              <option key={col} value={col}>{col}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Label>LSL (Lower Spec)</Label>
          <Input type="number" value={lsl} onChange={(e) => setLsl(e.target.value)} placeholder="e.g., 90" data-testid="lsl-input" />
        </div>

        <div className="space-y-2">
          <Label>USL (Upper Spec)</Label>
          <Input type="number" value={usl} onChange={(e) => setUsl(e.target.value)} placeholder="e.g., 110" data-testid="usl-input" />
        </div>

        <div className="flex items-end">
          <Button onClick={runCapabilityAnalysis} disabled={!selectedDataset || !capColumn || !lsl || !usl || analyzing} className="w-full" data-testid="run-capability-btn">
            {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Analyze"}
          </Button>
        </div>
      </div>

      {capResult && (
        <div className="space-y-6">
          <div className={`p-4 rounded-lg border ${
            capResult.assessment === "Capable" ? "border-[hsl(var(--success))] bg-[hsl(var(--success))]/10" :
            capResult.assessment === "Marginal" ? "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10" :
            "border-destructive bg-destructive/10"
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{capResult.assessment} Process</p>
                <p className="text-sm text-muted-foreground">
                  Sigma Level: {capResult.sigma_level} | Estimated PPM: {capResult.out_of_spec?.estimated_ppm ?? "N/A"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">Cpk = {capResult.capability_indices.cpk}</p>
                <p className="text-xs text-muted-foreground">Target: &ge; 1.33</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {["cp", "cpk", "pp", "ppk", "cpl", "cpu"].map((key) => (
              <div key={key} className="bg-background border border-border rounded-lg p-3">
                <p className="text-xs text-muted-foreground">{key === "cpl" ? "Cpl (Lower)" : key === "cpu" ? "Cpu (Upper)" : key.charAt(0).toUpperCase() + key.slice(1)}</p>
                <p className={`text-xl font-bold ${key === "cpk" ? (capResult.capability_indices.cpk >= 1.33 ? "text-[hsl(var(--success))]" : capResult.capability_indices.cpk >= 1.0 ? "text-[hsl(var(--warning))]" : "text-destructive") : ""}`}>
                  {capResult.capability_indices[key]}
                </p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">Process Statistics</h4>
              <div className="grid grid-cols-2 gap-3">
                {[["Mean", capResult.statistics.mean], ["Std Dev (Overall)", capResult.statistics.std_overall], ["Std Dev (Within)", capResult.statistics.std_within], ["Sample Size", capResult.statistics.n]].map(([label, val]) => (
                  <div key={label} className="bg-background border border-border rounded p-3">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="font-bold">{val}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">Out of Specification</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-background border border-border rounded p-3">
                  <p className="text-xs text-muted-foreground">Total Out of Spec</p>
                  <p className={`font-bold ${capResult.out_of_spec?.total_pct > 0 ? "text-destructive" : "text-[hsl(var(--success))]"}`}>{capResult.out_of_spec?.total_pct ?? 0}%</p>
                </div>
                <div className="bg-background border border-border rounded p-3">
                  <p className="text-xs text-muted-foreground">Estimated PPM</p>
                  <p className="font-bold">{capResult.out_of_spec?.estimated_ppm ?? "N/A"}</p>
                </div>
                <div className="bg-background border border-border rounded p-3">
                  <p className="text-xs text-muted-foreground">Above USL</p>
                  <p className="font-bold">{capResult.out_of_spec?.above_usl_pct ?? 0}%</p>
                </div>
                <div className="bg-background border border-border rounded p-3">
                  <p className="text-xs text-muted-foreground">Below LSL</p>
                  <p className="font-bold">{capResult.out_of_spec?.below_lsl_pct ?? 0}%</p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-background border border-border rounded-lg">
            <h4 className="font-medium mb-2">Specifications</h4>
            <div className="flex gap-8 text-sm">
              <span><span className="text-muted-foreground">LSL:</span> {capResult.specifications.lsl}</span>
              <span><span className="text-muted-foreground">Target:</span> {capResult.specifications.target}</span>
              <span><span className="text-muted-foreground">USL:</span> {capResult.specifications.usl}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
